import { useQuery } from 'react-query';
import { ProgressInfo } from '../components/ProgressBar';
import api from '../services/api';

export interface VODProgressResponse {
  vodId: string;
  progress: ProgressInfo | null;
  session: {
    createdAt: string;
    startedAt?: string;
    retryCount: number;
    error?: string;
  } | null;
}

export function useVODProgress(vodId: string, status: string, enabled: boolean = true) {
  return useQuery(
    ['vod-progress', vodId],
    async (): Promise<VODProgressResponse> => {
      const response = await api.get(`/api/vods/${vodId}/progress`);
      return response.data.data; // Extract data from ApiResponse wrapper
    },
    {
      enabled: enabled && status === 'processing',
      refetchInterval: status === 'processing' ? 2000 : false, // Poll every 2 seconds for processing VODs
      refetchIntervalInBackground: false,
      staleTime: 1000, // Consider data stale after 1 second
    }
  );
}

export function useMultipleVODProgress(vods: { id: string; status: string }[]) {
  const processingVods = vods.filter(vod => vod.status === 'processing');
  
  return useQuery(
    ['multiple-vod-progress', processingVods.map(v => v.id).join(',')],
    async (): Promise<Record<string, VODProgressResponse>> => {
      if (processingVods.length === 0) {
        return {};
      }

      const responses = await Promise.allSettled(
        processingVods.map(async (vod) => {
          const response = await api.get(`/api/vods/${vod.id}/progress`);
          return { vodId: vod.id, data: response.data.data };
        })
      );

      const result: Record<string, VODProgressResponse> = {};
      responses.forEach((response, index) => {
        if (response.status === 'fulfilled') {
          result[response.value.vodId] = response.value.data;
        } else {
          // For failed requests, provide empty progress
          result[processingVods[index].id] = {
            vodId: processingVods[index].id,
            progress: null,
            session: null,
          };
        }
      });

      return result;
    },
    {
      enabled: processingVods.length > 0,
      refetchInterval: processingVods.length > 0 ? 3000 : false, // Poll every 3 seconds for multiple VODs
      refetchIntervalInBackground: false,
      staleTime: 2000,
    }
  );
}